// ═══════════════════════════════════════════════════════
// Schoolizy — Espace Communication Parent
// Communiqués école · Emploi du temps · Déclaration d'absence
// ═══════════════════════════════════════════════════════
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useParent } from '../context/ParentContext';
import { useAuth } from '../context/AuthContext';

const TYPE_COLOR = {
  Résultat: 'green', Communiqué: 'blue', Événement: 'navy',
  Urgence: 'red', Information: 'gold', Autre: 'gray',
};

const DAYS_FR = ['', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const MOTIFS = ['Maladie', 'Urgence familiale', 'Rendez-vous médical', 'Décès dans la famille', 'Autre'];

export default function CommunicationParent() {
  const [tab, setTab] = useState('annonces');
  return (
    <>
      <div className="tabs">
        <button className={`tab ${tab === 'annonces' ? 'active' : ''}`} onClick={() => setTab('annonces')}>
          📢 Communiqués
        </button>
        <button className={`tab ${tab === 'edt' ? 'active' : ''}`} onClick={() => setTab('edt')}>
          📅 Emploi du temps
        </button>
        <button className={`tab ${tab === 'absence' ? 'active' : ''}`} onClick={() => setTab('absence')}>
          📝 Déclarer une absence
        </button>
      </div>
      {tab === 'annonces' && <Communiques />}
      {tab === 'edt'      && <EmploiDuTemps />}
      {tab === 'absence'  && <DeclarationAbsence />}
    </>
  );
}

// ── Communiqués ─────────────────────────────────────────

function Communiques() {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [view, setView]         = useState(null);
  const [typeFilter, setFilter] = useState('');

  useEffect(() => {
    api.get('/registry/annonces').then((d) => {
      const all = (Array.isArray(d) ? d : (d?.records || []))
        .map((r) => ({ id: r.id, ...r.data }))
        .filter((r) => r.status === 'Publiée' && ['Tous', 'Parents', undefined, ''].includes(r.audience))
        .sort((a, b) => {
          if (b.pinned !== a.pinned) return b.pinned ? 1 : -1;
          return (b.publishedAt || '').localeCompare(a.publishedAt || '');
        });
      setRows(all);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = rows.filter((r) => !typeFilter || r.type === typeFilter);
  const types    = [...new Set(rows.map((r) => r.type).filter(Boolean))];

  if (loading) return <div className="loading">Chargement…</div>;

  return (
    <>
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <div className="kpi"><span>Total</span><strong>{rows.length}</strong></div>
        <div className="kpi gold"><span>Épinglés</span><strong>{rows.filter((r) => r.pinned).length}</strong></div>
        <div className="kpi blue" style={{ '--kpi-accent': 'var(--primary)' }}>
          <span>Récents (30j)</span>
          <strong>{rows.filter((r) => r.publishedAt && new Date(r.publishedAt) > new Date(Date.now() - 30 * 864e5)).length}</strong>
        </div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h2>Communiqués de l'école</h2>
          {types.length > 1 && (
            <select value={typeFilter} onChange={(e) => setFilter(e.target.value)} style={{ width: 160 }}>
              <option value="">Tous les types</option>
              {types.map((t) => <option key={t}>{t}</option>)}
            </select>
          )}
        </div>
        <div className="panel-body">
          {filtered.length === 0 ? (
            <div className="empty">Aucun communiqué publié.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filtered.map((r) => (
                <div
                  key={r.id}
                  className="panel"
                  style={{ marginBottom: 0, cursor: 'pointer', border: r.pinned ? '1px solid var(--primary)' : undefined }}
                  onClick={() => setView(r)}
                >
                  <div style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                          {r.pinned && <span style={{ fontSize: 13 }}>📌</span>}
                          {r.type && <span className={`badge ${TYPE_COLOR[r.type] || 'gray'}`}>{r.type}</span>}
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{r.title}</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.4 }}>
                          {(r.content || '').slice(0, 160)}{r.content?.length > 160 ? '…' : ''}
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--muted-light)', marginLeft: 14, flexShrink: 0, textAlign: 'right' }}>
                        {r.publishedAt ? new Date(r.publishedAt).toLocaleDateString('fr-FR') : '—'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {view && (
        <div className="modal-overlay">
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              {view.type && <span className={`badge ${TYPE_COLOR[view.type] || 'gray'}`}>{view.type}</span>}
              {view.pinned && <span>📌</span>}
            </div>
            <h2 style={{ marginBottom: 8 }}>{view.title}</h2>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>
              Publié le {view.publishedAt ? new Date(view.publishedAt).toLocaleDateString('fr-FR') : '—'}
              {view.expiresAt && ` · Expire le ${new Date(view.expiresAt).toLocaleDateString('fr-FR')}`}
            </div>
            <div style={{ lineHeight: 1.8, whiteSpace: 'pre-wrap', fontSize: 13, color: 'var(--text)' }}>{view.content}</div>
            <div style={{ marginTop: 20 }}>
              <button className="btn-ghost" onClick={() => setView(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Emploi du temps ──────────────────────────────────────

function EmploiDuTemps() {
  const { children } = useParent();
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [slots, setSlots]             = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');

  const child = children[selectedIdx];
  const classId = child?.enrollments?.[0]?.classId;
  const className = child?.enrollments?.[0]?.class?.name || '—';

  useEffect(() => {
    if (!classId) return;
    setLoading(true);
    setError('');
    api.get(`/timetable?classId=${classId}`)
      .then((data) => setSlots(data || []))
      .catch((e) => setError(e.message || 'Emploi du temps indisponible.'))
      .finally(() => setLoading(false));
  }, [classId]);

  // Grouper par jour
  const byDay = {};
  for (let d = 1; d <= 6; d++) {
    const daySessions = slots.filter((s) => s.dayOfWeek === d).sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
    if (daySessions.length) byDay[d] = daySessions;
  }
  const activeDays = Object.keys(byDay).map(Number);

  if (children.length === 0) {
    return (
      <div className="panel">
        <div className="panel-body" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📅</div>
          <p style={{ color: 'var(--muted)', fontSize: 13 }}>Aucun enfant associé à votre compte.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Sélecteur enfant + infos classe */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        {children.length > 1 && (
          <div className="field" style={{ margin: 0, minWidth: 200 }}>
            <label>Enfant</label>
            <select value={selectedIdx} onChange={(e) => setSelectedIdx(Number(e.target.value))}>
              {children.map((c, i) => (
                <option key={c.id || i} value={i}>{c.lastName} {c.firstName}</option>
              ))}
            </select>
          </div>
        )}
        {child && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 14px', background: 'var(--surface-soft)', borderRadius: 8, fontSize: 13 }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%', background: 'var(--primary)', color: '#fff',
              display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0,
            }}>
              {child.firstName?.[0]}{child.lastName?.[0]}
            </div>
            <div>
              <div style={{ fontWeight: 600 }}>{child.lastName} {child.firstName}</div>
              <div style={{ color: 'var(--muted)', fontSize: 12 }}>{className} · {child.matricule}</div>
            </div>
          </div>
        )}
      </div>

      {/* Grille emploi du temps */}
      <div className="panel">
        <div className="panel-head">
          <h2>📅 Emploi du temps — {className}</h2>
        </div>
        <div className="panel-body">
          {loading && <div className="loading">Chargement…</div>}
          {error && <div className="form-error">{error}</div>}
          {!loading && !error && activeDays.length === 0 && (
            <div className="empty">L'emploi du temps n'a pas encore été publié.</div>
          )}
          {!loading && !error && activeDays.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${activeDays.length}, 1fr)`, gap: 10 }}>
              {activeDays.map((day) => (
                <div key={day}>
                  <div style={{
                    fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.04em',
                    color: 'var(--primary)', marginBottom: 8, paddingBottom: 6,
                    borderBottom: '2px solid var(--primary)',
                  }}>
                    {DAYS_FR[day]}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {byDay[day].map((s, i) => (
                      <div key={s.id || i} style={{
                        padding: '8px 10px', borderRadius: 8, fontSize: 12,
                        background: 'var(--surface-soft)', borderLeft: '3px solid var(--primary)',
                      }}>
                        <div style={{ fontWeight: 600, marginBottom: 2 }}>{s.subject?.name || '—'}</div>
                        <div className="mono" style={{ fontSize: 11, color: 'var(--muted)' }}>
                          {s.startTime} – {s.endTime}
                        </div>
                        {s.teacher && (
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                            {s.teacher.lastName} {s.teacher.firstName}
                          </div>
                        )}
                        {s.room && (
                          <div style={{ fontSize: 10, color: 'var(--muted-light)' }}>Salle {s.room.name}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Infos enseignants */}
      {!loading && slots.length > 0 && (() => {
        const teacherMap = {};
        slots.forEach((s) => {
          if (s.teacher?.id) teacherMap[s.teacher.id] = s.teacher;
        });
        const teacherList = Object.values(teacherMap);
        if (!teacherList.length) return null;
        return (
          <div className="panel" style={{ marginTop: 16 }}>
            <div className="panel-head"><h2>👩‍🏫 Enseignants</h2></div>
            <div className="panel-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                {teacherList.map((t) => {
                  const subjects = [...new Set(slots.filter((s) => s.teacher?.id === t.id).map((s) => s.subject?.name).filter(Boolean))];
                  return (
                    <div key={t.id} style={{ padding: '10px 12px', background: 'var(--surface-soft)', borderRadius: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{t.lastName} {t.firstName}</div>
                      {subjects.length > 0 && (
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>{subjects.join(', ')}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}

// ── Déclaration d'absence ────────────────────────────────

function DeclarationAbsence() {
  const { children }   = useParent();
  const { user }       = useAuth();
  const [declarations, setDeclarations] = useState([]);
  const [showForm, setShowForm]         = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const EMPTY = { studentId: children[0]?.id || '', dateDebut: today, dateFin: today, motif: MOTIFS[0], commentaire: '' };
  const [f, setF]         = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [success, setSuccess] = useState('');

  function load() {
    api.get('/parents/declarations-absence').then((d) => {
      setDeclarations((d || []).sort((a, b) => (b.dateDebut || '').localeCompare(a.dateDebut || '')));
    }).catch(() => {});
  }

  useEffect(() => { load(); }, []);
  useEffect(() => { setF((p) => ({ ...p, studentId: children[0]?.id || '' })); }, [children]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!f.studentId) { setError('Sélectionnez un enfant.'); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      const child = children.find((c) => c.id === f.studentId);
      const payload = {
        data: {
          ...f,
          parentEmail: user?.email,
          parentNom: `${user?.lastName} ${user?.firstName}`,
          studentNom: child ? `${child.lastName} ${child.firstName}` : '—',
          statut: 'En attente',
          soumisLe: new Date().toISOString(),
        },
        label: `Absence ${child?.lastName || ''} — ${f.dateDebut}`,
        searchText: `${child?.lastName} ${f.motif}`,
        refDate: f.dateDebut,
      };
      await api.post('/parents/declarations-absence', payload);
      setSuccess('Déclaration envoyée. L\'administration en sera informée.');
      setF(EMPTY);
      setShowForm(false);
      load();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  const childName = (id) => {
    const c = children.find((ch) => ch.id === id);
    return c ? `${c.lastName} ${c.firstName}` : '—';
  };

  const STATUT_COLOR = { 'En attente': 'gold', 'Acceptée': 'green', 'Refusée': 'red' };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <p style={{ fontSize: 13, color: 'var(--muted)' }}>
          Justifiez l'absence de votre enfant. L'administration validera votre déclaration.
        </p>
        {!showForm && (
          <button className="btn btn-sm" onClick={() => setShowForm(true)}>+ Nouvelle déclaration</button>
        )}
      </div>

      {showForm && (
        <div className="panel" style={{ marginBottom: 16, border: '1px solid var(--primary)' }}>
          <div className="panel-head"><h2>📝 Nouvelle déclaration d'absence</h2></div>
          <div className="panel-body">
            {error   && <div className="form-error">{error}</div>}
            <form onSubmit={submit}>
              <div className="grid-3" style={{ gap: 12 }}>
                {children.length > 1 && (
                  <div className="field" style={{ gridColumn: 'span 3' }}>
                    <label>Enfant concerné *</label>
                    <select value={f.studentId} onChange={set('studentId')}>
                      <option value="">Sélectionner…</option>
                      {children.map((c) => (
                        <option key={c.id} value={c.id}>{c.lastName} {c.firstName}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="field">
                  <label>Date de début *</label>
                  <input type="date" value={f.dateDebut} onChange={set('dateDebut')} min={today} required />
                </div>
                <div className="field">
                  <label>Date de fin *</label>
                  <input type="date" value={f.dateFin} onChange={set('dateFin')} min={f.dateDebut} required />
                </div>
                <div className="field">
                  <label>Motif *</label>
                  <select value={f.motif} onChange={set('motif')}>
                    {MOTIFS.map((m) => <option key={m}>{m}</option>)}
                  </select>
                </div>
                <div className="field" style={{ gridColumn: 'span 3' }}>
                  <label>Commentaire complémentaire</label>
                  <textarea value={f.commentaire} onChange={set('commentaire')} rows={3} placeholder="Précisez si nécessaire (médecin, hospitalisation…)" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button type="submit" className="btn" disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>
                  {saving ? 'Envoi…' : 'Envoyer la déclaration'}
                </button>
                <button type="button" className="btn-ghost" onClick={() => { setShowForm(false); setError(''); }}>Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {success && (
        <div className="form-success" style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--success-soft)', color: 'var(--success)', borderRadius: 8, fontSize: 13 }}>
          ✓ {success}
        </div>
      )}

      <div className="panel">
        <div className="panel-head"><h2>Mes déclarations</h2></div>
        <div className="panel-body" style={{ padding: 0 }}>
          {declarations.length === 0 ? (
            <div className="empty">Aucune déclaration pour le moment.</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Enfant</th><th>Période</th><th>Motif</th><th>Soumis le</th><th>Statut</th></tr>
                </thead>
                <tbody>
                  {declarations.map((d) => (
                    <tr key={d.id}>
                      <td><b>{d.studentNom || childName(d.studentId)}</b></td>
                      <td className="mono">
                        {d.dateDebut && new Date(d.dateDebut).toLocaleDateString('fr-FR')}
                        {d.dateFin && d.dateFin !== d.dateDebut && ` → ${new Date(d.dateFin).toLocaleDateString('fr-FR')}`}
                      </td>
                      <td>{d.motif}</td>
                      <td style={{ fontSize: 12, color: 'var(--muted)' }}>
                        {d.soumisLe ? new Date(d.soumisLe).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td><span className={`badge ${STATUT_COLOR[d.statut] || 'gray'}`}>{d.statut || 'En attente'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
